import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Award, Shield, Users, Target, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";

const AboutPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const values = [
    {
      icon: Award,
      title: "Minőség",
      description: "Csak a legmagasabb minőségű anyagokat és technikákat használjuk. Nincs kompromisszum."
    },
    {
      icon: Shield,
      title: "Garancia",
      description: "Munkáinkra garanciát vállalunk. Az Ön elégedettsége a mi sikerünk."
    },
    {
      icon: Users,
      title: "Szakértelem",
      description: "Csapatunk folyamatosan képzi magát a legújabb technikákból és termékekből."
    },
    {
      icon: Target,
      title: "Precizitás",
      description: "Minden részletre odafigyelünk. A tökéletesség a cél, nem csak az eredmény."
    }
  ];

  const timeline = [
    { year: "2014", event: "Megálmodtuk a prémium autókozmetika koncepcióját" },
    { year: "2015", event: "Megnyitottuk első műhelyünket Budapesten" },
    { year: "2017", event: "Bevezettük a kerámia bevonat szolgáltatást" },
    { year: "2019", event: "Elértük az 1000. elégedett ügyfelet" },
    { year: "2021", event: "Megújult műhely és kibővített csapat" },
    { year: "2024", event: "Prémium szolgáltatások, prémium eredmények" }
  ];

  const team = [
    {
      name: "Demo Szakértő 1",
      role: "Vezető Detailer (Referencia)",
      experience: "15+ év tapasztalat",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200&h=200&fit=crop"
    },
    {
      name: "Demo Szakértő 2",
      role: "Kerámia Specialista (Referencia)",
      experience: "10+ év tapasztalat",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=200&h=200&fit=crop"
    },
    {
      name: "Demo Szakértő 3",
      role: "Polírozás Mester (Referencia)",
      experience: "8+ év tapasztalat",
      image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=200&h=200&fit=crop"
    }
  ];

  return (
    <div data-testid="about-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">
              Rólunk
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#E5E5E5] mb-6">
              A SZENVEDÉLY
              <span className="block text-[#D4AF37]">AMELY HAJT MINKET</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg leading-relaxed">
              Több mint egy évtizede a prémium autókozmetika megszállottjai vagyunk. 
              Küldetésünk, hogy minden autó úgy ragyogjon, ahogy megérdemli.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="font-heading text-3xl text-[#E5E5E5] mb-6">
                TÖRTÉNETÜNK
              </h2>
              <div className="space-y-4 text-[#A3A3A3] leading-relaxed">
                <p>
                  Ez egy bemutató referencia oldal, amely példát mutat egy autókozmetikai 
                  vállalkozás professzionális webes megjelenésére.
                </p>
                <p>
                  A demo alapítója már fiatalon szenvedélyesen szerette az autókat. 
                  Évek óta tanulmányozta a világ legjobb detailing technikáit, mire 
                  megalapította vállalkozását.
                </p>
                <p>
                  Ma már büszkén mondhatjuk, hogy több ezer elégedett ügyfelünk van, 
                  és folyamatosan fejlődünk. Csapatunk a legmodernebb eszközökkel és 
                  prémium anyagokkal dolgozik, hogy minden autó a legjobb kézben legyen.
                </p>
              </div>
            </motion.div>

            <motion.div 
              {...fadeInUp}
              className="relative"
            >
              <div className="relative border border-[#2A2A2A] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1632823471565-1ecdf5c6f9e6?q=80&w=800&auto=format&fit=crop"
                  alt="Műhelyünk"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent opacity-60" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#D4AF37] p-6 text-black">
                <span className="font-heading text-4xl block">10+</span>
                <span className="text-sm uppercase tracking-wider">Év Tapasztalat</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl text-[#E5E5E5] mb-4">ÉRTÉKEINK</h2>
            <p className="text-[#A3A3A3]">Ezen elvek mentén dolgozunk minden nap</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#121212] border border-[#2A2A2A] p-8 text-center hover:border-[#D4AF37] transition-colors"
                data-testid={`value-${index}`}
              >
                <div className="w-16 h-16 bg-[#D4AF37] rounded-sm flex items-center justify-center mx-auto mb-6">
                  <value.icon size={32} className="text-black" />
                </div>
                <h3 className="font-heading text-xl text-[#E5E5E5] mb-3">{value.title}</h3>
                <p className="text-[#A3A3A3] text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl text-[#E5E5E5] mb-4">MÉRFÖLDKÖVEK</h2>
            <p className="text-[#A3A3A3]">Utunk az évek során</p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {timeline.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 mb-8 last:mb-0"
                data-testid={`timeline-${index}`}
              >
                <div className="flex-shrink-0 w-20">
                  <span className="font-heading text-2xl text-[#D4AF37]">{item.year}</span>
                </div>
                <div className="flex-grow pb-8 border-l-2 border-[#2A2A2A] pl-6 relative">
                  <div className="absolute left-0 top-0 w-4 h-4 bg-[#D4AF37] rounded-full -translate-x-[9px]" />
                  <p className="text-[#E5E5E5]">{item.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl text-[#E5E5E5] mb-4">CSAPATUNK</h2>
            <p className="text-[#A3A3A3]">Szakértő kezekben az Ön autója</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`team-member-${index}`}
              >
                <div className="relative w-40 h-40 mx-auto mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover rounded-sm border-2 border-[#2A2A2A] grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                <h3 className="font-heading text-xl text-[#E5E5E5] mb-1">{member.name}</h3>
                <p className="text-[#D4AF37] text-sm mb-1">{member.role}</p>
                <p className="text-[#666] text-sm">{member.experience}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="font-heading text-3xl text-[#E5E5E5] mb-6">
                MIÉRT MINKET<br />
                <span className="text-[#D4AF37]">VÁLASSZON?</span>
              </h2>
              <ul className="space-y-4">
                {[
                  "Több mint 10 év szakmai tapasztalat",
                  "Prémium minőségű anyagok és eszközök",
                  "Garancia minden munkánkra",
                  "Precíz, határidőre elkészülő munka",
                  "Személyre szabott szolgáltatások",
                  "Átlátható árazás, rejtett költségek nélkül"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-[#D4AF37] flex-shrink-0" />
                    <span className="text-[#A3A3A3]">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div {...fadeInUp}>
              <div className="bg-[#121212] border border-[#2A2A2A] p-8">
                <h3 className="font-heading text-2xl text-[#E5E5E5] mb-4">
                  Készen áll a prémium élményre?
                </h3>
                <p className="text-[#A3A3A3] mb-6">
                  Foglaljon időpontot most, és bízza ránk autója szépségét. 
                  Csapatunk már várja!
                </p>
                <Link to="/idopontfoglalas">
                  <Button
                    data-testid="about-cta-btn"
                    className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] py-4 rounded-sm"
                  >
                    Időpont Foglalás
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
